import { useMemo } from 'react';
import { ControlBar } from '../components/meeting/ControlBar';
import { MeetingHeader } from '../components/meeting/MeetingHeader';
import { RoomCameraView } from '../components/meeting/RoomCameraView';
import { ScreenShareView } from '../components/meeting/ScreenShareView';
import { VideoGrid } from '../components/meeting/VideoGrid';
import { Spinner } from '../components/common/Spinner';
import { useMeetingStore } from '../stores/meetingStore';

interface MeetingContentProps {
  roomCode: string;
  roomName: string;
  onLeave?: () => void;
}

/**
 * Layout container for the active meeting.
 */
export function MeetingContent({ roomCode, roomName, onLeave }: MeetingContentProps) {
  const {
    participants,
    localParticipant,
    room,
    screenShareOwnerId,
    screenShareTrack,
    stopScreenShare,
  } = useMeetingStore();

  const startTime = useMemo(() => room?.createdAt ?? new Date(), [room?.createdAt]);
  const participantCount = participants.length + (localParticipant ? 1 : 0);
  const maxParticipants = room?.maxParticipants ?? 16;

  const { screenShareStream, sharerName, isLocalShare } = useScreenShareData(
    participants,
    localParticipant,
    screenShareOwnerId,
    screenShareTrack
  );

  const handlePopoutParticipants = () => {
    const url = `/meeting/${roomCode}/participants`;
    window.open(url, 'participants', 'width=1200,height=800');
  };

  const handlePopoutScreenShare = () => {
    const url = `/meeting/${roomCode}/screenshare`;
    window.open(url, 'screenshare', 'width=1400,height=900');
  };

  return (
    <div className="h-screen flex flex-col bg-bg-primary overflow-hidden">
      <MeetingHeader
        roomName={roomName}
        roomCode={roomCode}
        participantCount={participantCount}
        maxParticipants={maxParticipants}
        isHost={localParticipant?.isHost ?? false}
        startTime={startTime}
        onPopoutParticipants={handlePopoutParticipants}
        onPopoutScreenShare={handlePopoutScreenShare}
        hasScreenShare={!!screenShareStream}
      />
      <MeetingMain
        screenShareStream={screenShareStream}
        sharerName={sharerName}
        isLocalShare={isLocalShare}
        onStopShare={isLocalShare ? stopScreenShare : undefined}
      />
      <ControlBar onLeave={onLeave} />
    </div>
  );
}

interface MeetingMainProps {
  screenShareStream: MediaStream | null;
  sharerName: string;
  isLocalShare: boolean;
  onStopShare?: () => void;
}

/**
 * Main meeting layout for screen share, room camera, and participant grid.
 */
function MeetingMain({
  screenShareStream,
  sharerName,
  isLocalShare,
  onStopShare,
}: MeetingMainProps) {
  const hasShare = !!screenShareStream;
  const layoutClass = hasShare
    ? 'grid gap-4 px-6 py-5 flex-1 min-h-0 overflow-auto lg:grid-cols-[2.1fr,1fr]'
    : 'px-6 py-5 flex-1 min-h-0 overflow-auto';

  return (
    <main className={layoutClass}>
      {hasShare && (
        <div className="min-h-[40vh] h-[40vh] lg:h-auto lg:min-h-0">
          <ScreenShareView
            stream={screenShareStream}
            sharerName={sharerName}
            isLocal={isLocalShare}
            onStopShare={onStopShare}
          />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <RoomCameraView />
        <VideoGrid />
      </div>
    </main>
  );
}

/**
 * Pulls screen share data from meeting state.
 */
function useScreenShareData(
  participants: { participantId: string; displayName: string }[],
  localParticipant: { participantId: string; displayName: string } | null,
  screenShareOwnerId: string | null,
  screenShareTrack: MediaStreamTrack | null
) {
  const sharerName = useMemo(() => {
    if (!screenShareOwnerId) {
      return '';
    }

    const sharer =
      localParticipant?.participantId === screenShareOwnerId
        ? localParticipant
        : participants.find((participant) => participant.participantId === screenShareOwnerId);

    return sharer?.displayName || 'Participant';
  }, [participants, localParticipant, screenShareOwnerId]);

  const screenShareStream = useMemo(() => {
    if (!screenShareTrack) {
      return null;
    }

    return new MediaStream([screenShareTrack]);
  }, [screenShareTrack]);

  const isLocalShare = screenShareOwnerId === localParticipant?.participantId;

  return { screenShareStream, sharerName, isLocalShare };
}

/**
 * Loading state while joining the meeting.
 */
export function ConnectingState() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-bg-primary">
      <div className="card max-w-md w-full p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <div>
            <h2 className="text-xl font-semibold">Joining meeting...</h2>
            <p className="text-gray-400">Setting up your audio, video, and space.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Error state displayed when join fails.
 */
export function ErrorState({ error }: { error: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-bg-primary">
      <div className="card max-w-md w-full p-8 text-center">
        <div className="text-danger text-xl mb-3">{error}</div>
        <p className="text-gray-400">Redirecting to home...</p>
      </div>
    </div>
  );
}
